import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { getAvailableLanguages } from '../../i18n/languages.js';
import Button from './Button.js';

const LanguageSwitcher = ({ className = '', variant = 'compact' }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const languageOptions = useMemo(() => {
    const options = t('language.options', { returnObjects: true });
    return options && typeof options === 'object' ? options : {};
  }, [t, i18n.language]);

  const languages = useMemo(
    () => getAvailableLanguages(i18n).filter((lang) => languageOptions[lang.code]),
    [i18n, languageOptions]
  );

  const current = languages.find((l) => l.code === i18n.language) || languages[0];
  const getLabel = (lang) => languageOptions[lang?.code] || lang?.native;

  useEffect(() => {
    if (variant !== 'compact') return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [variant]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(!open)}
        className="gap-1.5 text-gray-600"
        aria-label={t('language.choose')}
        title={t('language.label')}
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline text-xs">{getLabel(current)}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1 max-h-64 overflow-y-auto">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                i18n.language === lang.code ? 'text-blue-700 font-semibold bg-blue-50/50' : 'text-gray-700'
              }`}
            >
              {getLabel(lang)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
